import { computed, inject, type Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { resolveLocale, splitLocale, type Locale } from '@shared/models/auth';
import { AuthUser } from '@tvd/data-access/auth';
import { map } from 'rxjs';

/**
 * A reactive, locale-safe formatting locale for pipes and components.
 *
 * The language follows the active UI language — the picker switches via
 * `translate.use()` → `onLangChange`, so we bridge that to a signal. The
 * region/culture stays anchored to the user's stored locale (or the browser),
 * so a `de-CH` user keeps CH formatting when toggling language. `authUser.locale`
 * alone can't drive this: it's the *saved* profile, only PATCHed async on switch.
 *
 * Must be called in an injection context.
 */
export function injectActiveLocale(): Signal<Locale> {
  const translate = inject(TranslateService);
  const authUser = inject(AuthUser);

  const lang = toSignal(translate.onLangChange.pipe(map((e) => e.lang)), {
    initialValue: translate.getCurrentLang(),
  });

  return computed(() => {
    const activeLang = lang();
    const stored =
      authUser.data()?.locale ?? translate.getBrowserCultureLang();
    const culture = stored ? splitLocale(stored).culture : '';
    return resolveLocale(activeLang ? `${activeLang}-${culture}` : stored);
  });
}
